import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { brandAndCountryFromZone } from "@seek/audience-zones";
import { date, scrollToError, useShowFeature, PrivacyLevel, useGetUrl, CredentialStatus } from "@seek/ca-myprofile-utils";
import { createForm } from "@seek/forms-ui";
import { rules } from "@seek/validators-js";
import { useTranslations } from "@vocab/react";
import { HiddenVisually, IconNewWindow, IconDelete, Dialog, Text, Actions, Button, Columns, Column, Autosuggest, useResponsiveValue, Stack, MonthPicker, Checkbox, Textarea, Dropdown, IconSubCategory, Box, TextField, IconCritical, Inline, Strong, Badge, TextLink, Card, Heading, RadioGroup, RadioItem, FieldMessage, Alert, TextLinkButton, Toggle } from "braid-design-system";
import * as React from "react";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useCompatibleLanguage, ProfileMonthPickerRenderer } from "./styles/components/ProfileMonthPicker/ProfileMonthPickerRenderer/ProfileMonthPickerRenderer.mjs";
import { translations } from "./components/DeleteConfirmationDialog/.vocab/index.mjs";
import { translations as translations$1 } from "./components/FormActionButtons/.vocab/index.mjs";
import { useLazyQuery, useQuery } from "@apollo/client";
import { translations as translations$2 } from "./components/ProfileAutosuggest/.vocab/index.mjs";
import gql from "graphql-tag";
import debounce from "lodash/debounce.js";
import { v4 } from "uuid";
import { ProfileMonthPickerToggleRenderer } from "./styles/components/ProfileMonthPicker/ProfileMonthPickerToggleRenderer/ProfileMonthPickerToggleRenderer.mjs";
import { translations as translations$3 } from "./components/CareerHistoryForm/.vocab/index.mjs";
import { translations as translations$4 } from "./components/ClassificationDropdown/.vocab/index.mjs";
import { qualificationLevelOptions, nationalityOptions, getCountryCallingCodeById, countryCallingCodeOptions, yearsOfExperienceOptions } from "@seek/profile-taxonomies";
import { translations as translations$5 } from "./components/EducationForm/.vocab/index.mjs";
import { EducationEndDate } from "./styles/components/EducationForm/EducationEndDate/EducationEndDate.mjs";
import { translations as translations$6 } from "./components/LicenceForm/.vocab/index.mjs";
import { translations as translations$7 } from "./components/NationalityDropdown/.vocab/index.mjs";
import { translations as translations$8 } from "./components/PhoneNumberCountryCallingCode/.vocab/index.mjs";
import { translations as translations$9 } from "./components/PrivacyDropdown/.vocab/index.mjs";
import { FancyDropdown } from "./styles/components/PrivacyDropdown/FancyDropdown/FancyDropdown.mjs";
import { focusElement, ProfileFormCardOverlayContent } from "./styles/components/ProfileFormCardOverlayContent/ProfileFormCardOverlayContent.mjs";
import { ProfileFormCardContent } from "./styles/components/ProfileFormCardContent/ProfileFormCardContent.mjs";
import { translations as translations$a } from "./components/ProfileFormCard/.vocab/index.mjs";
import { translations as translations$b } from "./components/ProfileVisibilitySection/.vocab/index.mjs";
import { translations as translations$c } from "./components/SkillsForm/.vocab/index.mjs";
import { getSuggestedSkills, MAX_SKILLS, getValidationValue as getValidationValue$2, maxSkills, getSkillsStructuredData, Skills } from "./styles/components/SkillsForm/Skills/Skills.mjs";
import { SuggestedSkills } from "./styles/components/SkillsForm/SuggestedSkills/SuggestedSkills.mjs";
import { VerificationLogo, EducationLogo } from "@seek/certsy-ui";
import { useDialogHistoryManager, CertsyVerifiedIndicator, DefinitionList, CertsyDialog } from "@seek/profile-base-ui";
import { translations as translations$d } from "./components/VerifiedForm/.vocab/index.mjs";
import { translations as translations$e } from "./components/VerifiedLicenceForm/.vocab/index.mjs";
import { translations as translations$f } from "./components/VerifiedEducationForm/.vocab/index.mjs";
import { translations as translations$g } from "./components/YearsOfExperience/.vocab/index.mjs";
const DeleteConfirmationDialog = ({
  displayDialog,
  setDisplayDialog,
  onDelete,
  cardName,
  dataAutomationSuffix,
  verifiedType,
  customMessage
}) => {
  const { t } = useTranslations(translations);
  const isVerified = Boolean(verifiedType);
  const defaultDialogTitle = t("Delete card", { cardName });
  const verifiedDialogTitle = t("Unshare on SEEK Pass");
  const defaultDialogMessage = t("Are you sure you want to delete this?");
  const verifiedDialogMessage = {
    education: t("To remove this from your profile...education"),
    credential: t("To remove this from your profile...credential")
  };
  const defaultDeleteButtonText = t("Delete cardName", {
    cardName,
    HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v })
  });
  const verifiedDeleteButtonText = /* @__PURE__ */ jsx(Fragment, { children: t("SEEK Pass sharing settings", {
    HiddenText: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v }, "certsySharingSetting")
  }) });
  const deleteIcon = isVerified ? /* @__PURE__ */ jsx(
    IconNewWindow,
    {
      alignY: "lowercase",
      title: t("Opens in a new tab"),
      titleId: "certsySharingSettingsLinkIcon"
    }
  ) : /* @__PURE__ */ jsx(IconDelete, {});
  const handleDelete = () => {
    onDelete();
    setDisplayDialog(false);
  };
  const optionalDataAutomationSuffix = dataAutomationSuffix ? `-${dataAutomationSuffix}` : "";
  const dialogMessage = customMessage ?? (verifiedType ? verifiedDialogMessage[verifiedType] : defaultDialogMessage);
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      id: "deleteConfirmationDialog",
      open: displayDialog,
      title: isVerified ? verifiedDialogTitle : defaultDialogTitle,
      onClose: () => setDisplayDialog(false),
      width: "xsmall",
      data: { automation: "delete-confirmation-dialog" },
      closeLabel: t("Close"),
      children: [
        /* @__PURE__ */ jsx(Text, { children: dialogMessage }),
        /* @__PURE__ */ jsxs(Actions, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              onClick: handleDelete,
              tone: "critical",
              icon: deleteIcon,
              iconPosition: isVerified ? "trailing" : "leading",
              data: {
                automation: `confirm-delete${optionalDataAutomationSuffix}`
              },
              children: isVerified ? verifiedDeleteButtonText : defaultDeleteButtonText
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              onClick: () => setDisplayDialog(false),
              variant: "transparent",
              tone: "formAccent",
              data: { automation: `cancel-delete${optionalDataAutomationSuffix}` },
              children: t("Cancel delete card", {
                cardName,
                HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v }, "cancelDeleteCard")
              })
            }
          )
        ] })
      ]
    }
  );
};
const FormActionButtons = ({
  cardName,
  onCancel,
  onDeleteClick,
  onSubmit,
  dataAutomationSuffix,
  readonly = false
}) => {
  const { t } = useTranslations(translations$1);
  const optionalDataAutomationSuffix = dataAutomationSuffix ? `${dataAutomationSuffix}-` : "";
  return /* @__PURE__ */ jsxs(Columns, { collapseBelow: "tablet", space: "small", children: [
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Actions, { children: readonly ? /* @__PURE__ */ jsx(
      Button,
      {
        tone: "formAccent",
        onClick: onCancel,
        data: {
          automation: `${optionalDataAutomationSuffix}done-button`
        },
        children: t("Done")
      }
    ) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          tone: "formAccent",
          data: {
            automation: `${optionalDataAutomationSuffix}save-button`
          },
          ...onSubmit && { onClick: onSubmit },
          children: t("Save cardName", {
            cardName,
            HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v })
          })
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          tone: "formAccent",
          variant: "transparent",
          onClick: onCancel,
          data: {
            automation: `${optionalDataAutomationSuffix}cancel-button`
          },
          children: t("Cancel edit cardName", {
            cardName,
            HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v })
          })
        }
      )
    ] }) }) }),
    /* @__PURE__ */ jsx(Column, { width: "content", children: onDeleteClick && /* @__PURE__ */ jsx(
      Button,
      {
        tone: "critical",
        variant: "transparent",
        icon: /* @__PURE__ */ jsx(IconDelete, {}),
        onClick: onDeleteClick,
        data: {
          automation: `${optionalDataAutomationSuffix}delete-button`
        },
        children: t("Delete please confirm", {
          cardName,
          HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v })
        })
      }
    ) })
  ] });
};
const count = 10;
const getRoleTitle = gql`
  query getRoleTitle(
    $query: String!
    $countryCode: CountryCodeIso2!
    $languageCode: LanguageCodeIso!
    $session: String!
  ) {
    ontologyRoleTitlesSeniority(
      query: $query
      count: ${count}
      countryCode2: $countryCode
      languageCode: $languageCode
      session: $session
    ) {
      id
      seniorityId
      matchedSeniorityVariant
      normalisedText
    }
  }
`;
const getOrganisations = gql`
  query getOrganisations(
    $query: String!
    $countryCode: CountryCodeIso2!
    $languageCode: LanguageCodeIso!
    $session: String!
  ) {
    ontologyOrganisations(
      query: $query
      count: ${count}
      countryCode2: $countryCode
      languageCode: $languageCode
      session: $session
    ) {
      id
      normalisedText
    }
  }
`;
const getQualifications = gql`
  query getQualifications(
    $query: String!
    $countryCode: CountryCodeIso2!
    $languageCode: LanguageCodeIso!
    $session: String!
  ) {
    ontologyQualifications(
      query: $query
      count: ${count}
      countryCode2: $countryCode
      languageCode: $languageCode
      session: $session
    ) {
      id
      normalisedText
    }
  }
`;
const getEducationProviders = gql`
  query getEducationProviders(
    $query: String!
    $countryCode: CountryCodeIso2!
    $languageCode: LanguageCodeIso!
    $session: String!
  ) {
    ontologyEducationProviders(
      query: $query
      count: ${count}
      countryCode2: $countryCode
      languageCode: $languageCode
      session: $session
    ) {
      id
      normalisedText
      countries {
        id
        isoCode
        name
      }
    }
  }
`;
const getSpecReqs = gql`
  query getSpecReqs(
    $query: String!
    $session: String!
    $profileField: SpecReqProfileField!
    $countryCode: CountryCodeIso2!
    $languageCode: LanguageCodeIso!
  ) {
    ontologySpecReqs(
      query: $query
      count: ${count}
      session: $session
      profileField: $profileField
      countryCode: $countryCode
      languageCode: $languageCode
    ) {
      id
      normalisedText
      collectExpiry
    }
  }
`;
const trackSelected = gql`
  query trackSelected(
    $id: String!
    $session: String!
    $index: Int!
    $ontologyType: OntologyType!
  ) {
    ontologyTrackSelected(
      id: $id
      session: $session
      index: $index
      ontologyType: $ontologyType
    ) {
      id
    }
  }
`;
const getProfileLocationSuggest = gql`
  query getProfileLocationSuggest(
    $query: String!
    $session: String!
    $zone: Zone!
    $locationType: ProfileLocationType!
    $languageCode: LanguageCodeIso
  ) {
    profileLocationSuggest(
      query: $query
      count: ${count}
      session: $session
      zone: $zone
      locationType: $locationType
      languageCode: $languageCode
    ) {
      ... on ProfileSuggestLocationGroup {
            label
            suggestions {
              id
              contextualName
              shortName
              countryCode
              kind
            }
          }
      ... on ProfileSuggestLocation {
            id
            contextualName
            shortName
            countryCode
            kind
      }
    }
  }
`;
const profileLocationTrackSelected = gql`
  query profileLocationTrackSelected($id: Int!, $session: String!) {
    profileLocationTrackSelected(id: $id, session: $session) {
      id
    }
  }
`;
const getRightToWorkOptions = gql`
  query getRightToWorkOptions(
    $query: String!
    $languageCode: LanguageCodeIso!
    $zone: Zone!
    $source: SourceExperience!
  ) {
    rightToWorkOptions(
      languageCode: $languageCode
      zone: $zone
      platform: WEB
      source: $source
      filter: { countryNameQuery: $query }
    ) {
      country {
        countryCode
        name
      }
      options {
        id
        description
        formattedDescription
        verificationUrl
      }
    }
  }
`;
const assetToReturnType = {
  RoleTitle: "ontologyRoleTitlesSeniority",
  Organisation: "ontologyOrganisations",
  Qualification: "ontologyQualifications",
  EducationProvider: "ontologyEducationProviders",
  SpecReq: "ontologySpecReqs",
  ProfileLocation: "profileLocationSuggest",
  RightToWorkOptions: "rightToWorkOptions"
};
const assetToQuery = {
  RoleTitle: getRoleTitle,
  Organisation: getOrganisations,
  Qualification: getQualifications,
  EducationProvider: getEducationProviders,
  SpecReq: getSpecReqs,
  ProfileLocation: getProfileLocationSuggest,
  RightToWorkOptions: getRightToWorkOptions
};
const assetToTrackingType = {
  RoleTitle: "ROLETITLES_SENIORITY",
  Organisation: "ORGANISATIONS",
  Qualification: "QUALIFICATIONS",
  EducationProvider: "EDUCATION_PROVIDERS",
  SpecReq: "SPECREQS"
};
const findMatchingSuggestion = ({
  text,
  suggestions
}) => {
  const allSuggestions = [];
  suggestions.forEach((suggestion) => {
    if ("suggestions" in suggestion) {
      allSuggestions.push(...suggestion.suggestions);
    } else {
      allSuggestions.push(suggestion);
    }
  });
  const matchingSuggestion = allSuggestions.filter(
    ({ text: suggestionText }) => suggestionText === text
  );
  return matchingSuggestion[0];
};
const getStructuredData$2 = (data) => data;
const formatSuggestions = (suggestions) => suggestions.map(({ id: value, normalisedText: text, ...rest }) => ({
  value,
  text,
  ...rest
}));
const formatRightToWorkOptionsSuggestions = (suggestions) => suggestions.map(({ country, options }) => ({
  text: country.name,
  value: country.countryCode,
  country,
  options
}));
const formatProfileLocationSuggestions = ({
  id: value,
  contextualName: text,
  ...rest
}) => ({
  value,
  text,
  ...rest
});
const formatProfileLocationGroupSuggestions = ({
  suggestions,
  ...rest
}) => ({
  suggestions: suggestions.map(
    (suggestion) => formatProfileLocationSuggestions(suggestion)
  ),
  ...rest
});
const formatProfileLocationSuggestionsData = (suggestionData) => suggestionData.map(
  (data) => "suggestions" in data ? formatProfileLocationGroupSuggestions(
    data
  ) : formatProfileLocationSuggestions(data)
);
const processNoSuggestionsMessage = ({
  autosuggestValue,
  suggestions,
  showNoResultsMessage,
  noResultsMessage,
  suggestionsLoading
}) => {
  const emptySuggestions = suggestions.length === 0;
  const hasInputText = autosuggestValue.text !== "";
  const hasValueId = Boolean(autosuggestValue.value);
  const shouldShowNoResultsMessage = Boolean(
    emptySuggestions && showNoResultsMessage && hasInputText && !hasValueId && !suggestionsLoading
  );
  return shouldShowNoResultsMessage ? noResultsMessage : void 0;
};
const getSuggestionData = (asset, data) => {
  const responseDataKey = assetToReturnType[asset];
  switch (asset) {
    case "ProfileLocation":
      return formatProfileLocationSuggestionsData(
        data[responseDataKey]
      );
    case "RightToWorkOptions":
      return formatRightToWorkOptionsSuggestions(
        data[responseDataKey]
      );
    default:
      return formatSuggestions(
        data[responseDataKey]
      );
  }
};
const useSuggestions = ({
  asset,
  locale,
  queryVariables,
  apolloClient,
  profileLocationProps: profileLocationQueryVariables,
  rightToWorkOptionsProps
}) => {
  const language = useCompatibleLanguage();
  const [querySuggestions, { data, loading }] = useLazyQuery(assetToQuery[asset], {
    fetchPolicy: "no-cache",
    ...apolloClient ? { client: apolloClient } : {}
  });
  const [suggestions, setSuggestions] = useState([]);
  const clearSuggestions = useCallback(() => setSuggestions([]), []);
  const [session, setSession] = useState(v4());
  const endSession = useCallback(() => setSession(v4()), []);
  useEffect(() => {
    if (!loading && data) {
      const suggestionData = getSuggestionData(asset, data);
      setSuggestions(suggestionData);
    }
  }, [asset, data, loading]);
  const fetchSuggestions = useMemo(
    () => debounce(
      (suggestionValue) => {
        querySuggestions({
          variables: {
            query: suggestionValue,
            session,
            languageCode: language,
            ...locale ? { countryCode: locale } : {},
            ...queryVariables,
            ...profileLocationQueryVariables,
            ...rightToWorkOptionsProps
          }
        });
      },
      150,
      { leading: true }
    ),
    [
      querySuggestions,
      session,
      language,
      locale,
      queryVariables,
      profileLocationQueryVariables,
      rightToWorkOptionsProps
    ]
  );
  return {
    fetchSuggestions,
    suggestions,
    clearSuggestions,
    session,
    endSession,
    suggestionsLoading: loading
  };
};
const useProfileLocation = (zone) => {
  const { t } = useTranslations(translations$2);
  const [, countryCode] = brandAndCountryFromZone(zone) || [null, "AU"];
  const createCurrentLocationPlaceholderText = () => {
    switch (countryCode) {
      case "AU":
      case "NZ":
        return t("Enter suburb or town");
      case "HK":
      case "SG":
        return t("Enter district or area");
      case "TH":
        return t("Enter province or district");
      case "ID":
        return t("Enter district or city");
      case "PH":
        return t("Enter city or town");
      case "MY":
        return t("Enter district or town");
      default:
        return t("Enter location");
    }
  };
  const createProfileLocationPlaceholderText = ({
    locationType
  }) => {
    if (locationType === "Preferred") {
      return t("Enter location");
    }
    return createCurrentLocationPlaceholderText();
  };
  const createNoLocationFoundMessage = () => {
    const NO_LOCATION_FOUND = t("No location found.");
    const SEARCHING_SUBURB_TOWN_OR_CITY = t(
      "Try searching for another location such as suburb, town or city."
    );
    switch (countryCode) {
      case "AU":
      case "NZ":
        return {
          title: NO_LOCATION_FOUND,
          description: SEARCHING_SUBURB_TOWN_OR_CITY
        };
      case "HK":
      case "SG":
        return {
          title: NO_LOCATION_FOUND,
          description: t(
            "Try searching for another location such as district, neighbourhood or area."
          )
        };
      case "ID":
        return {
          title: NO_LOCATION_FOUND,
          description: t(
            "Try searching for another location such as district, city or village."
          )
        };
      case "TH":
        return {
          title: NO_LOCATION_FOUND,
          description: t(
            "Try searching for another location such as province, district or city."
          )
        };
      case "PH":
        return {
          title: NO_LOCATION_FOUND,
          description: t(
            "Try searching for another location such as city, town, region or province."
          )
        };
      case "MY":
        return {
          title: NO_LOCATION_FOUND,
          description: t(
            "Try searching for another location such as district, city, town or area."
          )
        };
      default:
        return {
          title: NO_LOCATION_FOUND,
          description: SEARCHING_SUBURB_TOWN_OR_CITY
        };
    }
  };
  return { createProfileLocationPlaceholderText, createNoLocationFoundMessage };
};
const ProfileAutosuggest = ({
  id,
  label,
  value = { text: "" },
  queryVariables,
  asset,
  message,
  locale,
  getStructuredData: getStructuredData2,
  onChange,
  onSuggestionSelect,
  tone,
  onBlur,
  onClear,
  data,
  scrollToTopOnMobile,
  placeholder,
  inputRef,
  showNoResultsMessage,
  apolloClient,
  profileLocationProps,
  rightToWorkOptionsProps
}) => {
  const { t } = useTranslations(translations$2);
  const { zone = "anz-1", locationType = "Home" } = profileLocationProps || {};
  const { createProfileLocationPlaceholderText, createNoLocationFoundMessage } = useProfileLocation(zone);
  const trackingQuery = asset === "ProfileLocation" ? profileLocationTrackSelected : trackSelected;
  const [trackSelected$1] = useLazyQuery(trackingQuery, {
    ...apolloClient ? { client: apolloClient } : {}
  });
  const {
    fetchSuggestions,
    suggestions,
    clearSuggestions,
    session,
    endSession,
    suggestionsLoading
  } = useSuggestions({
    asset,
    locale,
    queryVariables,
    profileLocationProps,
    rightToWorkOptionsProps
  });
  const handleTrackingOnSuggestionSelect = (matchingSuggestion, currentSuggestions) => {
    if (asset === "RightToWorkOptions") {
      return;
    }
    if (asset === "ProfileLocation") {
      trackSelected$1({
        variables: {
          id: Number(matchingSuggestion.value),
          session
        }
      });
    } else {
      const defaultCurrentSuggestions = currentSuggestions;
      const index = defaultCurrentSuggestions.findIndex(
        (suggestion) => suggestion.value === matchingSuggestion.value
      );
      trackSelected$1({
        variables: {
          id: matchingSuggestion.value,
          session,
          index,
          ontologyType: assetToTrackingType[asset]
        }
      });
    }
  };
  const handleChange = (autosuggestValue) => {
    const { text: inputText, value: selectedValueId } = autosuggestValue;
    const currentSuggestions = suggestions;
    const matchingSuggestion = findMatchingSuggestion({
      text: inputText,
      suggestions: currentSuggestions
    });
    if (inputText && !matchingSuggestion) {
      fetchSuggestions(inputText);
    }
    let structuredData = {};
    if (matchingSuggestion) {
      handleTrackingOnSuggestionSelect(matchingSuggestion, currentSuggestions);
      structuredData = (getStructuredData2 == null ? void 0 : getStructuredData2(matchingSuggestion)) || getStructuredData$2(matchingSuggestion);
    }
    const hasSelectedValue = Boolean(selectedValueId);
    onChange({
      ...structuredData,
      text: inputText
    });
    if (hasSelectedValue && onSuggestionSelect) {
      onSuggestionSelect == null ? void 0 : onSuggestionSelect({ ...structuredData, text: inputText });
    }
  };
  const handleBlur = () => {
    endSession();
    onBlur == null ? void 0 : onBlur(value);
    if (value.text === "") {
      clearSuggestions();
    }
  };
  const noResultsMessage = asset === "ProfileLocation" && profileLocationProps ? createNoLocationFoundMessage() : t("No results found.");
  const noSuggestionsMessage = processNoSuggestionsMessage({
    autosuggestValue: value,
    suggestions,
    showNoResultsMessage,
    noResultsMessage,
    suggestionsLoading
  });
  const processedPlaceholder = asset === "ProfileLocation" && profileLocationProps ? createProfileLocationPlaceholderText({ locationType }) : void 0;
  return /* @__PURE__ */ jsx(
    Autosuggest,
    {
      id,
      label,
      placeholder: placeholder ?? processedPlaceholder,
      message,
      data,
      scrollToTopOnMobile,
      tone,
      suggestions,
      noSuggestionsMessage,
      value,
      onChange: handleChange,
      onBlur: handleBlur,
      reserveMessageSpace: true,
      ref: inputRef,
      onClear,
      suggestionHighlight: "remaining"
    }
  );
};
const getStructuredData$1 = ({
  value,
  seniorityId,
  matchedSeniorityVariant
}) => ({
  ontologyId: value,
  ...seniorityId ? { seniorityId } : {},
  ...matchedSeniorityVariant ? { matchedSeniorityVariant } : {}
});
const initialOntologyData = {
  text: "",
  ontologyId: ""
};
const initialValues$3 = {
  title: initialOntologyData,
  company: initialOntologyData,
  seniority: initialOntologyData,
  stillInRole: false,
  from: {},
  to: {},
  achievements: ""
};
const setOntologyDataInitialState = (name, data) => ({
  text: (data == null ? void 0 : data.text) || initialValues$3[name].text,
  ontologyId: (data == null ? void 0 : data.ontologyId) || initialValues$3[name].ontologyId
});
const getInitialState$2 = (role) => {
  const initialState = { ...initialValues$3 };
  if (!role) {
    return initialState;
  }
  const { to } = role;
  const isValidEndDate = date.isValidMonth(to || {});
  return {
    ...initialState,
    ...role,
    title: setOntologyDataInitialState("title", role.title),
    company: setOntologyDataInitialState("company", role.company),
    seniority: setOntologyDataInitialState("seniority", role.seniority),
    stillInRole: !isValidEndDate,
    to: to && isValidEndDate ? to : {},
    achievements: role.achievements || initialState.achievements
  };
};
const getOntologySelectionValidationValue = ({ text }) => text;
const mapFormStateToProps$4 = ({
  form: { dirty },
  values: { stillInRole }
}) => ({
  dirty,
  stillInRole
});
const CAREER_HISTORY_FORM = "CAREER_HISTORY_FORM";
const { FormProvider: FormProvider$5, useField: useField$5 } = createForm(CAREER_HISTORY_FORM, {
  formType: "braid"
});
const achievementsRules = rules.roleAchievements();
const Form$5 = ({
  zone = "anz-1",
  dirty,
  stillInRole,
  onSubmit,
  onCancel,
  handleSubmit,
  achievementsRef,
  setFieldValue,
  onFormDirtyChange,
  scrollToTopOnMobile = true,
  apolloClient,
  onDelete
}) => {
  const { t } = useTranslations(translations$3);
  const initialRender = useRef(true);
  const lineLimit = useResponsiveValue()({
    mobile: 10,
    tablet: 16
  });
  const [, localeSuperset] = brandAndCountryFromZone(zone) || [null, "AU"];
  const locale = localeSuperset;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };
  useEffect(() => {
    if (onFormDirtyChange && !initialRender.current) {
      onFormDirtyChange(dirty);
    }
    return () => {
      if (onFormDirtyChange && dirty) onFormDirtyChange(false);
    };
  }, [dirty, onFormDirtyChange]);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const clearTitle = useCallback(
    () => setFieldValue("title", initialValues$3.title),
    [setFieldValue]
  );
  const clearCompany = useCallback(
    () => setFieldValue("company", initialValues$3.company),
    [setFieldValue]
  );
  const onSubmitFn = useCallback(
    () => handleSubmit((values) => {
      onSubmit({ dirty, values });
    }),
    [handleSubmit, onSubmit, dirty]
  );
  const autoSuggestProps = {
    locale,
    getStructuredData: getStructuredData$1,
    scrollToTopOnMobile
  };
  const titleField = useField$5({
    id: "title",
    ...rules.roleTitle({ requiredLabel: t("Job title") }),
    getValidationValue: getOntologySelectionValidationValue
  });
  const companyField = useField$5({
    id: "company",
    ...rules.companyName({ requiredLabel: t("Company name") }),
    getValidationValue: getOntologySelectionValidationValue
  });
  const fromField = useField$5({
    id: "from",
    ...rules.roleStartDate({
      stillInRoleFieldName: "stillInRole",
      beforeFieldName: "to",
      fieldLabel: t("Started"),
      beforeFieldLabel: t("Ended")
    })
  });
  const toField = useField$5({
    id: "to",
    ...rules.roleEndDate({
      stillInRoleFieldName: "stillInRole",
      afterFieldName: "from",
      fieldLabel: t("Ended"),
      afterFieldLabel: t("Started")
    })
  });
  const { value: stillInRoleFieldValue, ...stillInRoleField } = useField$5({
    id: "stillInRole"
  });
  const achievementsField = useField$5({
    id: "achievements",
    ...achievementsRules
  });
  return /* @__PURE__ */ jsxs("form", { onSubmit: onSubmitFn(), children: [
    /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsx(
          ProfileAutosuggest,
          {
            id: "title",
            label: t("Job title"),
            data: {
              automation: "role-title"
            },
            asset: "RoleTitle",
            onClear: clearTitle,
            apolloClient,
            ...autoSuggestProps,
            ...titleField
          }
        ),
        /* @__PURE__ */ jsx(
          ProfileAutosuggest,
          {
            id: "company",
            label: t("Company name"),
            data: {
              automation: "company-name"
            },
            asset: "Organisation",
            onClear: clearCompany,
            apolloClient,
            ...autoSuggestProps,
            ...companyField
          }
        ),
        /* @__PURE__ */ jsx(ProfileMonthPickerRenderer, { "data-automation": "career-history-start-date", children: ({ monthPickerProps }) => /* @__PURE__ */ jsx(
          MonthPicker,
          {
            id: "from",
            label: t("Started"),
            reserveMessageSpace: true,
            ...fromField,
            ...monthPickerProps
          }
        ) }),
        /* @__PURE__ */ jsx(
          ProfileMonthPickerToggleRenderer,
          {
            hideMonthPicker: stillInRole,
            checkbox: /* @__PURE__ */ jsx(
              Checkbox,
              {
                id: "stillInRole",
                label: t("Still in role"),
                data: { automation: "still-in-role" },
                checked: stillInRoleFieldValue,
                ...stillInRoleField
              }
            ),
            children: ({ monthPickerProps }) => /* @__PURE__ */ jsx(
              MonthPicker,
              {
                id: "to",
                label: t("Ended"),
                reserveMessageSpace: true,
                ...toField,
                ...monthPickerProps,
                value: toField.value || initialValues$3.to
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Textarea,
          {
            id: "achievements",
            label: t("Description"),
            secondaryLabel: t("recommended"),
            description: t(
              "Summarise your responsibilities, skills and achievements."
            ),
            characterLimit: achievementsRules.validatorProps.max,
            ref: achievementsRef,
            data: {
              automation: "career-history-achievements",
              "hj-masked": "true"
            },
            lines: 6,
            lineLimit: lineLimit ?? 16,
            reserveMessageSpace: true,
            ...achievementsField,
            value: achievementsField.value || initialValues$3.achievements,
            highlightRanges: achievementsField.highlightRanges || void 0
          }
        ),
        /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t(
          "Stay safe. Don’t include sensitive personal information such as..."
        ) })
      ] }),
      /* @__PURE__ */ jsx(
        FormActionButtons,
        {
          cardName: t("role"),
          dataAutomationSuffix: "career-history",
          onCancel,
          ...onDelete && { onDeleteClick }
        }
      )
    ] }),
    onDelete && /* @__PURE__ */ jsx(
      DeleteConfirmationDialog,
      {
        displayDialog: showDeleteDialog,
        setDisplayDialog: setShowDeleteDialog,
        onDelete,
        cardName: t("role"),
        dataAutomationSuffix: "role"
      }
    )
  ] });
};
const CareerHistoryForm = (props) => {
  const language = useCompatibleLanguage();
  return /* @__PURE__ */ jsx(
    FormProvider$5,
    {
      mapFormStateToProps: mapFormStateToProps$4,
      initialValues: getInitialState$2(props.initialValues),
      onSubmitFail: props.onSubmitFail ? props.onSubmitFail : (errors) => scrollToError(errors),
      validateOnMount: props.validateOnMount,
      language,
      children: (formProps) => /* @__PURE__ */ jsx(Form$5, { ...formProps, ...props })
    }
  );
};
const mapDropdownItemToOption = (item, index) => /* @__PURE__ */ jsx("option", { value: item.value, children: item.label }, index);
const getClassificationOptions = gql`
  query getClassificationOptions(
    $zone: Zone!
    $languageCode: LanguageCodeIso!
  ) {
    preferredClassificationOptions(zone: $zone, languageCode: $languageCode) {
      id
      description
      subClassifications {
        id
        description
      }
    }
  }
`;
const formatMessage = (message, valid, value) => {
  const isSubClassificationMessage = Boolean(value.id);
  return {
    classificationMessage: !valid && !isSubClassificationMessage ? message : null,
    subClassificationMessage: !valid && isSubClassificationMessage ? message : null
  };
};
const ClassificationDropdown = React.forwardRef(
  ({
    onChange,
    value,
    id,
    autoFocus,
    message = "",
    valid = true,
    label,
    secondaryLabel,
    zone,
    apolloClient
  }, ref) => {
    var _a, _b;
    const { t } = useTranslations(translations$4);
    const languageCode = useCompatibleLanguage();
    const { data, error: getPreferredClassificationOptionsError } = useQuery(getClassificationOptions, {
      variables: {
        zone,
        languageCode
      },
      ...apolloClient ? { client: apolloClient } : {}
    });
    const preferredClassificationOptions = useMemo(
      () => (data == null ? void 0 : data.preferredClassificationOptions) || [],
      [data]
    );
    const classificationDropdownOptions = useMemo(
      () => preferredClassificationOptions.map((classificationItem) => ({
        value: classificationItem.id,
        label: classificationItem.description
      })).map(mapDropdownItemToOption),
      [preferredClassificationOptions]
    );
    const getSubClassificationDropdownOptions = useCallback(
      (classificationId) => {
        const preferredClassification = preferredClassificationOptions.find(
          (item) => item.id === classificationId
        );
        const subClassification = (preferredClassification == null ? void 0 : preferredClassification.subClassifications) || [];
        return subClassification.map((subClassificationItem) => ({
          value: subClassificationItem.id.toString(),
          label: subClassificationItem.description
        }));
      },
      [preferredClassificationOptions]
    );
    useEffect(() => {
      setSubClassifications(getSubClassificationDropdownOptions(value == null ? void 0 : value.id));
    }, [
      getSubClassificationDropdownOptions,
      preferredClassificationOptions,
      value == null ? void 0 : value.id
    ]);
    const [subClassifications, setSubClassifications] = useState(getSubClassificationDropdownOptions(value == null ? void 0 : value.id));
    const handleClassificationChange = useCallback(
      (event) => {
        const classificationId = parseInt(event.currentTarget.value, 10);
        const newSubClassifications = getSubClassificationDropdownOptions(classificationId);
        setSubClassifications(newSubClassifications);
        onChange({
          id: classificationId
        });
      },
      [getSubClassificationDropdownOptions, onChange]
    );
    const handleSubClassificationChange = useCallback(
      (event) => {
        const subClassificationId = parseInt(event.currentTarget.value, 10);
        onChange({
          id: value.id,
          subClassification: { id: subClassificationId }
        });
      },
      [onChange, value.id]
    );
    const { classificationMessage, subClassificationMessage } = formatMessage(
      message,
      valid,
      value
    );
    const validClassification = valid || classificationMessage === null;
    const validSubClassification = valid || subClassificationMessage === null;
    return /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(
        Dropdown,
        {
          id: `${id}-primary`,
          label: label ?? t("Classification"),
          tone: validClassification ? "neutral" : "critical",
          message: classificationMessage,
          value: ((_a = value == null ? void 0 : value.id) == null ? void 0 : _a.toString()) || "",
          onChange: handleClassificationChange,
          autoFocus,
          data: { automation: "classification", "hj-masked": "true" },
          ref,
          placeholder: t("Select classification"),
          reserveMessageSpace: true,
          disabled: getPreferredClassificationOptionsError !== void 0,
          children: classificationDropdownOptions
        }
      ),
      /* @__PURE__ */ jsx(
        Dropdown,
        {
          id: `${id}-secondary`,
          label: secondaryLabel ?? t("Sub-classification"),
          icon: /* @__PURE__ */ jsx(IconSubCategory, {}),
          tone: validSubClassification ? "neutral" : "critical",
          message: subClassificationMessage,
          value: ((_b = value == null ? void 0 : value.subClassification) == null ? void 0 : _b.id.toString()) || "",
          onChange: handleSubClassificationChange,
          data: { automation: "sub-classification", "hj-masked": "true" },
          placeholder: t("Select sub-classification"),
          reserveMessageSpace: true,
          disabled: getPreferredClassificationOptionsError !== void 0,
          children: subClassifications.map(mapDropdownItemToOption)
        }
      )
    ] });
  }
);
ClassificationDropdown.displayName = "ClassificationDropdown";
const mapTaxonomyToDropdown = ({
  id,
  description
}) => ({
  label: description,
  value: id
});
const VerifiedDescription = ({
  id,
  label,
  description,
  secondaryLabel,
  type,
  descriptionField,
  characterLimit
}) => {
  const lineLimit = useResponsiveValue()({
    mobile: 10,
    tablet: 16
  });
  return /* @__PURE__ */ jsx(
    Textarea,
    {
      id,
      label,
      secondaryLabel,
      description,
      data: {
        automation: `${type}-${id}`,
        "hj-masked": "true"
      },
      characterLimit,
      lines: 6,
      lineLimit: lineLimit ?? 16,
      reserveMessageSpace: true,
      ...descriptionField,
      highlightRanges: descriptionField.highlightRanges || void 0,
      value: descriptionField.value || ""
    }
  );
};
const getInitialExpectedCompletionYear = (completed, completionDate) => {
  if (typeof completionDate === "undefined") {
    return void 0;
  }
  if (completed) {
    return void 0;
  }
  return completionDate.year;
};
const setInitialValues = (values) => ({
  ...values,
  ...values.completionDate === null ? { completionDate: void 0 } : { completionDate: values.completionDate }
});
const initialValues$2 = {
  id: "",
  institute: {
    text: ""
  },
  level2: "",
  name: {
    text: ""
  },
  completed: true,
  completionDate: void 0,
  highlights: ""
};
const getInitialState$1 = (state) => {
  if (!state) {
    return initialValues$2;
  }
  return setInitialValues({
    ...initialValues$2,
    ...state,
    name: {
      ...state.name
    }
  });
};
const getInstituteStructuredData = ({ value }) => ({
  ontologyId: value
});
const getQualificationStructuredData = ({
  value
}) => ({ ontologyId: value });
const getValidationValue$1 = ({ text }) => text;
const getVerifiedInitialState$1 = (qualification) => ({
  highlights: (qualification == null ? void 0 : qualification.highlights) ?? ""
});
const educationFormUpdateToEducationUpdate = (educationUpdate) => {
  const { id, name, institute, completed, completionDate, highlights, level2 } = educationUpdate;
  return {
    id,
    name,
    institute,
    completed,
    completionDate,
    highlights,
    level2
  };
};
const mapFormStateToProps$3 = ({
  form: { dirty },
  values: { name, completed, completionDate },
  touched: { name: qualificationTouched }
}) => ({
  dirty,
  name,
  completed,
  qualificationTouched,
  initialExpectedCompletionYear: getInitialExpectedCompletionYear(
    completed,
    completionDate
  )
});
const { FormProvider: FormProvider$4, useField: useField$4 } = createForm(
  "EDUCATION_FORM",
  { formType: "braid" }
);
const highlightRules = rules.highlights();
const expectedCompletionDateRules = rules.expectedCompletionDate();
const Form$4 = ({
  zone = "anz-1",
  completed,
  dirty,
  initialExpectedCompletionYear,
  className,
  handleSubmit,
  onSubmit,
  onCancel,
  setFieldValue,
  onFormDirtyChange,
  scrollToTopOnMobile = true,
  apolloClient,
  isUnifiedSite,
  onDelete
}) => {
  const { t } = useTranslations(translations$5);
  const instituteRules = rules.institute({ requiredLabel: t("Institution") });
  const levelRules = rules.level({ requiredLabel: t("Qualification level") });
  const majorRules = rules.major({ requiredLabel: t("Major") });
  const nameRules = rules.qualification({
    requiredLabel: t("Course or qualification")
  });
  const [, localeSuperset] = brandAndCountryFromZone(zone) || [null, "AU"];
  const locale = localeSuperset;
  const initialRender = useRef(true);
  const [levelOptions, setLevelOptions] = useState([]);
  const language = useCompatibleLanguage();
  const showFeature = useShowFeature(zone, language, isUnifiedSite);
  const showQualificationLevelMajor = showFeature("qualification-level-major");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  useEffect(() => {
    let shouldSetLevelOptions = true;
    qualificationLevelOptions(locale, language).then(
      (options) => {
        if (shouldSetLevelOptions) {
          setLevelOptions(options);
        }
      }
    );
    return () => {
      shouldSetLevelOptions = false;
    };
  }, [locale, language]);
  useEffect(() => {
    if (!initialRender.current) {
      setFieldValue("completionDate", void 0);
    }
  }, [completed, setFieldValue]);
  useEffect(() => {
    if (onFormDirtyChange && !initialRender.current) {
      onFormDirtyChange(dirty);
    }
    return () => {
      if (onFormDirtyChange && dirty) onFormDirtyChange(false);
    };
  }, [dirty, onFormDirtyChange]);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const clearInstitute = useCallback(
    () => setFieldValue("institute", initialValues$2.institute),
    [setFieldValue]
  );
  const clearName = useCallback(
    () => setFieldValue("name", initialValues$2.name),
    [setFieldValue]
  );
  const onSubmitFn = useCallback(
    () => handleSubmit(
      (values) => onSubmit({
        dirty,
        values: educationFormUpdateToEducationUpdate(values)
      })
    ),
    [handleSubmit, onSubmit, dirty]
  );
  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };
  const autosuggestProps = {
    locale,
    reserveMessageSpace: true,
    scrollToTopOnMobile
  };
  const instituteField = useField$4({
    id: "institute",
    ...instituteRules,
    getValidationValue: getValidationValue$1
  });
  const level2Field = useField$4({
    enabled: showQualificationLevelMajor,
    id: "level2",
    ...levelRules
  });
  const nameField = useField$4({
    id: "name",
    ...showQualificationLevelMajor ? majorRules : nameRules,
    getValidationValue: getValidationValue$1
  });
  const { value: completedValue, ...completedField } = useField$4({
    id: "completed"
  });
  const completionDateField = useField$4({
    id: "completionDate",
    ...completed ? {} : { ...expectedCompletionDateRules }
  });
  const highlightsField = useField$4({
    id: "highlights",
    ...highlightRules
  });
  return /* @__PURE__ */ jsxs(Box, { component: "form", className, onSubmit: onSubmitFn(), children: [
    /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        showQualificationLevelMajor ? /* @__PURE__ */ jsxs(Columns, { space: "medium", collapseBelow: "tablet", children: [
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
            Dropdown,
            {
              id: "level2",
              label: t("Qualification level"),
              placeholder: t("Select the qualification level"),
              data: { automation: "level2" },
              ...level2Field,
              value: level2Field.value || initialValues$2.level2 || "",
              reserveMessageSpace: true,
              children: levelOptions.map(mapTaxonomyToDropdown).map(mapDropdownItemToOption)
            }
          ) }),
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
            TextField,
            {
              id: "name",
              label: t("Major"),
              data: {
                automation: "qualification-major"
              },
              ...nameField,
              onChange: (event) => {
                setFieldValue("name", {
                  text: event.target.value,
                  ontologyId: void 0
                });
              },
              value: nameField.value.text,
              reserveMessageSpace: true
            }
          ) })
        ] }) : /* @__PURE__ */ jsx(
          ProfileAutosuggest,
          {
            id: "name",
            label: t("Course or qualification"),
            getStructuredData: getQualificationStructuredData,
            data: {
              automation: "qualification"
            },
            asset: "Qualification",
            onClear: clearName,
            apolloClient,
            ...autosuggestProps,
            ...nameField
          }
        ),
        /* @__PURE__ */ jsx(
          ProfileAutosuggest,
          {
            id: "institute",
            label: t("Institution"),
            getStructuredData: getInstituteStructuredData,
            data: {
              automation: "institute"
            },
            asset: "EducationProvider",
            onClear: clearInstitute,
            apolloClient,
            ...autosuggestProps,
            ...instituteField
          }
        ),
        /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          /* @__PURE__ */ jsx(
            Checkbox,
            {
              id: "completed",
              label: t("Qualification complete"),
              data: { automation: "qualification-completed" },
              checked: completedValue,
              ...completedField
            }
          ),
          /* @__PURE__ */ jsx(
            EducationEndDate,
            {
              completed,
              initialExpectedCompletionYear,
              ...completionDateField
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          VerifiedDescription,
          {
            id: "highlights",
            type: "qualification",
            label: t("Course highlights"),
            secondaryLabel: t("optional"),
            description: t("Add activities, projects, awards or..."),
            characterLimit: highlightRules.validatorProps.max,
            descriptionField: highlightsField
          }
        ),
        /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t(
          "Stay safe. Don’t include sensitive personal information such as..."
        ) })
      ] }),
      /* @__PURE__ */ jsx(
        FormActionButtons,
        {
          cardName: t("education"),
          dataAutomationSuffix: "education",
          onCancel,
          ...onDelete && { onDeleteClick }
        }
      )
    ] }),
    onDelete && /* @__PURE__ */ jsx(
      DeleteConfirmationDialog,
      {
        displayDialog: showDeleteDialog,
        setDisplayDialog: setShowDeleteDialog,
        onDelete,
        cardName: t("education"),
        dataAutomationSuffix: "education"
      }
    )
  ] });
};
const EducationForm = (props) => {
  const language = useCompatibleLanguage();
  return /* @__PURE__ */ jsx(
    FormProvider$4,
    {
      mapFormStateToProps: mapFormStateToProps$3,
      initialValues: getInitialState$1(props.initialValues),
      onSubmitFail: props.onSubmitFail ? props.onSubmitFail : (errors) => scrollToError(errors),
      validateOnMount: props.validateOnMount,
      language,
      children: (formProps) => /* @__PURE__ */ jsx(Form$4, { ...formProps, ...props })
    }
  );
};
const initialValues$1 = {
  name: {
    text: ""
  },
  issuingOrganisation: "",
  issueDate: {},
  expiryDate: {},
  noExpiryDate: false,
  description: ""
};
const getInitialState = (licence) => {
  if (!licence) {
    return initialValues$1;
  }
  return {
    ...initialValues$1,
    ...licence
  };
};
const getVerifiedInitialState = (licence) => ({ description: (licence == null ? void 0 : licence.description) ?? "" });
const getStructuredData = ({
  value,
  collectExpiry
}) => ({
  ontologyId: value,
  collectExpiry
});
const getValidationValue = ({ text }) => text;
const isExpired = (expiryDate) => {
  const today = /* @__PURE__ */ new Date();
  const todayInMonths = today.getFullYear() * 12 + today.getMonth() + 1;
  const expiredInMonths = expiryDate.year * 12 + expiryDate.month;
  return expiredInMonths < todayInMonths;
};
const expiredHelpText = "This date indicates the credential has expired";
const showExpiryHelpText = ({
  value
}) => {
  if (typeof (value == null ? void 0 : value.month) !== "undefined" && (value == null ? void 0 : value.year)) {
    if (isExpired(value)) {
      return { message: expiredHelpText };
    }
  }
};
const mapAutosuggestToServer = ({
  text,
  ontologyId
}) => ({
  ...ontologyId ? { ontologyId } : {},
  text
});
const licenceFormUpdateToLicenceUpdate = (licenceUpdate) => {
  const { issueDate: issueDateUpdate, expiryDate: expiryDateUpdate } = licenceUpdate;
  const issueDate = (issueDateUpdate == null ? void 0 : issueDateUpdate.month) && (issueDateUpdate == null ? void 0 : issueDateUpdate.year) ? { month: issueDateUpdate.month, year: issueDateUpdate == null ? void 0 : issueDateUpdate.year } : void 0;
  const expiryDate = !licenceUpdate.noExpiryDate && (expiryDateUpdate == null ? void 0 : expiryDateUpdate.month) && (expiryDateUpdate == null ? void 0 : expiryDateUpdate.year) ? { month: expiryDateUpdate.month, year: expiryDateUpdate == null ? void 0 : expiryDateUpdate.year } : void 0;
  return {
    name: mapAutosuggestToServer(licenceUpdate.name),
    ...licenceUpdate.issuingOrganisation ? { issuingOrganisation: licenceUpdate.issuingOrganisation } : {},
    issueDate,
    expiryDate,
    noExpiryDate: licenceUpdate.noExpiryDate,
    ...licenceUpdate.description ? { description: licenceUpdate.description } : {}
  };
};
const mapFormStateToProps$2 = ({
  form: { dirty },
  values: { noExpiryDate }
}) => ({
  dirty,
  noExpiryDate
});
const LICENCE_FORM$1 = "LICENCE_FORM";
const { FormProvider: FormProvider$3, useField: useField$3 } = createForm(
  LICENCE_FORM$1,
  { formType: "braid" }
);
const issuingOrganisationRules = rules.licenceIssuingOrganisation();
const descriptionRules$1 = rules.licenceDescription();
const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
const Form$3 = ({
  onSubmit,
  onCancel,
  dirty,
  onFormDirtyChange,
  handleSubmit,
  noExpiryDate,
  scrollToTopOnMobile,
  setFieldValue,
  locale = "AU",
  apolloClient,
  onDelete
}) => {
  const { t } = useTranslations(translations$6);
  const initialRender = useRef(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };
  useEffect(() => {
    if (onFormDirtyChange && !initialRender.current) {
      onFormDirtyChange(dirty);
    }
    return () => {
      if (onFormDirtyChange && dirty) onFormDirtyChange(false);
    };
  }, [dirty, onFormDirtyChange]);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const onSubmitFn = useCallback(
    () => handleSubmit((values) => {
      onSubmit({ dirty, values: licenceFormUpdateToLicenceUpdate(values) });
    }),
    [handleSubmit, onSubmit, dirty]
  );
  const clearName = useCallback(
    () => setFieldValue("name", initialValues$1.name),
    [setFieldValue]
  );
  const nameField = useField$3({
    id: "name",
    ...rules.licenceName({ requiredLabel: t("Licence or certification name") }),
    getValidationValue
  });
  useEffect(() => {
    const collectExpiry = nameField.value.collectExpiry;
    if (typeof collectExpiry !== "undefined") {
      setFieldValue("noExpiryDate", !collectExpiry);
    }
  }, [nameField.value.collectExpiry, setFieldValue]);
  const issuingOrganisationField = useField$3({
    id: "issuingOrganisation",
    ...issuingOrganisationRules
  });
  const issueDateField = useField$3({
    id: "issueDate",
    ...rules.licenceIssueDate({
      noExpiryDateFieldName: "noExpiryDate",
      beforeFieldName: "expiryDate",
      fieldLabel: t("Issue date"),
      beforeFieldLabel: t("Expiry date")
    })
  });
  const expiryDateField = useField$3({
    id: "expiryDate",
    ...rules.licenceExpiryDate({
      noExpiryDateFieldName: "noExpiryDate",
      afterFieldName: "issueDate",
      fieldLabel: t("Expiry date"),
      afterFieldLabel: t("Issue date")
    })
  });
  const expiryHelpText = showExpiryHelpText(expiryDateField);
  const { value: noExpiryDateValue, ...noExpiryDateField } = useField$3({
    id: "noExpiryDate"
  });
  const descriptionField = useField$3({
    id: "description",
    ...descriptionRules$1
  });
  return /* @__PURE__ */ jsxs("form", { onSubmit: onSubmitFn(), children: [
    /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsx(
          ProfileAutosuggest,
          {
            id: "name",
            label: t("Licence or certification name"),
            asset: "SpecReq",
            queryVariables: {
              profileField: "LicencesCertifications"
            },
            onClear: clearName,
            getStructuredData,
            scrollToTopOnMobile,
            placeholder: t("e.g. Drivers Licence"),
            locale,
            apolloClient,
            ...nameField
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            id: "issuingOrganisation",
            label: t("Issuing organisation"),
            secondaryLabel: t("optional"),
            reserveMessageSpace: true,
            ...issuingOrganisationField,
            value: issuingOrganisationField.value || initialValues$1.issuingOrganisation || ""
          }
        ),
        /* @__PURE__ */ jsx(ProfileMonthPickerRenderer, { "data-automation": "licence-issue-date", children: ({ monthPickerProps }) => /* @__PURE__ */ jsx(
          MonthPicker,
          {
            id: "issueDate",
            label: t("Issue date"),
            secondaryLabel: t("optional"),
            reserveMessageSpace: true,
            ...issueDateField,
            ...monthPickerProps
          }
        ) }),
        /* @__PURE__ */ jsx(
          ProfileMonthPickerToggleRenderer,
          {
            hideMonthPicker: noExpiryDate,
            checkbox: /* @__PURE__ */ jsx(
              Checkbox,
              {
                id: "noExpiryDate",
                label: t("No expiry"),
                data: { automation: "no-expiry-date" },
                checked: noExpiryDateValue,
                ...noExpiryDateField
              }
            ),
            children: ({ monthPickerProps }) => /* @__PURE__ */ jsx(
              MonthPicker,
              {
                id: "expiryDate",
                label: t("Expiry date"),
                secondaryLabel: t("recommended"),
                minYear: currentYear - 10,
                maxYear: currentYear + 100,
                ascendingYears: true,
                reserveMessageSpace: true,
                ...expiryDateField,
                ...expiryHelpText,
                ...monthPickerProps
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          VerifiedDescription,
          {
            id: "description",
            type: "licence",
            label: t("Description"),
            secondaryLabel: t("optional"),
            description: t("Briefly describe this credential..."),
            descriptionField
          }
        ),
        /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t(
          "Stay safe. Don’t include sensitive personal information such as..."
        ) })
      ] }),
      /* @__PURE__ */ jsx(
        FormActionButtons,
        {
          cardName: t("licence or certification"),
          dataAutomationSuffix: "licence",
          onCancel,
          ...onDelete && { onDeleteClick }
        }
      )
    ] }),
    onDelete && /* @__PURE__ */ jsx(
      DeleteConfirmationDialog,
      {
        displayDialog: showDeleteDialog,
        setDisplayDialog: setShowDeleteDialog,
        onDelete,
        cardName: t("licence or certification"),
        dataAutomationSuffix: "licence"
      }
    )
  ] });
};
const LicenceForm = (props) => {
  const language = useCompatibleLanguage();
  return /* @__PURE__ */ jsx(
    FormProvider$3,
    {
      mapFormStateToProps: mapFormStateToProps$2,
      initialValues: getInitialState(props.initialValues),
      onSubmitFail: props.onSubmitFail ? props.onSubmitFail : (errors) => scrollToError(errors),
      validateOnMount: props.validateOnMount,
      language,
      children: (formProps) => /* @__PURE__ */ jsx(Form$3, { ...formProps, ...props })
    }
  );
};
const mapDropdownItemsToOptgroup = (option, index) => /* @__PURE__ */ jsx("optgroup", { label: option.label, children: option.value.map(mapDropdownItemToOption) }, index);
const useCommonNationalityOptions = (language) => {
  const [commonNationalityOptions, setCommonNationalityOptions] = useState([]);
  useEffect(() => {
    let shouldSetCommonNationalityOptions = true;
    nationalityOptions("common", language).then((options) => {
      if (options && shouldSetCommonNationalityOptions) {
        setCommonNationalityOptions(options);
      }
    });
    return () => {
      shouldSetCommonNationalityOptions = false;
    };
  }, [language]);
  return commonNationalityOptions;
};
const useAllNationalityOptions = (language) => {
  const [allNationalityOptions, setAllNationalityOptions] = useState([]);
  useEffect(() => {
    let shouldSetAllNationalityOptions = true;
    nationalityOptions("all", language).then((options) => {
      if (shouldSetAllNationalityOptions) {
        setAllNationalityOptions(options);
      }
    });
    return () => {
      shouldSetAllNationalityOptions = false;
    };
  }, [language]);
  return allNationalityOptions;
};
const NationalityDropdown = ({
  value,
  onChange,
  tone,
  message,
  showOptionalLabel = true
}) => {
  const { t } = useTranslations(translations$7);
  const language = useCompatibleLanguage();
  const commonNationalityOptions = useCommonNationalityOptions(language);
  const allNationalityOptions = useAllNationalityOptions(language);
  const nationalityDropdownOptions = useMemo(
    () => /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "optgroup",
        {
          "data-testid": "commonNationality",
          label: t("Commonly selected"),
          children: commonNationalityOptions.map(mapTaxonomyToDropdown).map(mapDropdownItemToOption)
        }
      ),
      /* @__PURE__ */ jsx("optgroup", { "data-testid": "allNationality", label: t("All"), children: allNationalityOptions.map(mapTaxonomyToDropdown).map(mapDropdownItemToOption) })
    ] }),
    [allNationalityOptions, commonNationalityOptions, t]
  );
  const handleNationalityChange = (event) => {
    const newNationality = { id: event.currentTarget.value };
    onChange(newNationality);
  };
  return /* @__PURE__ */ jsx(Columns, { space: ["none", "small"], alignY: "bottom", collapseBelow: "tablet", children: /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
    Dropdown,
    {
      id: "nationality",
      label: t("Nationality"),
      secondaryLabel: showOptionalLabel && t("optional"),
      description: t("Select a country of nationality"),
      placeholder: t("Select country..."),
      data: { automation: "nationality" },
      value: (value == null ? void 0 : value.id) ?? "",
      onChange: handleNationalityChange,
      tone,
      message,
      reserveMessageSpace: true,
      children: nationalityDropdownOptions
    }
  ) }) });
};
const useCommonCountryCallingCodeOptions = (language) => {
  const [commonCountryCallingCodeOptions, setCommonCountryCallingCodeOptions] = useState([]);
  if (commonCountryCallingCodeOptions.length === 0) {
    countryCallingCodeOptions("common", language).then((options) => {
      if (options) {
        setCommonCountryCallingCodeOptions(options);
      }
    });
  }
  return commonCountryCallingCodeOptions;
};
const useAllCountryCallingCodeOptions = (language) => {
  const [allCountryCallingCodeOptions, setAllCountryCallingCodeOptions] = useState([]);
  if (allCountryCallingCodeOptions.length === 0) {
    countryCallingCodeOptions("all", language).then((options) => {
      if (options) {
        setAllCountryCallingCodeOptions(options);
      }
    });
  }
  return allCountryCallingCodeOptions;
};
const PhoneNumberCountryCallingCode = React.forwardRef(
  ({
    phoneNumber,
    onPhoneNumberChange,
    countryCallingCode: countryCallingCodeValue,
    onCountryCallingCodeChange,
    secondaryLabel,
    message,
    tone
  }, ref) => {
    const { t } = useTranslations(translations$8);
    const language = useCompatibleLanguage();
    const [countryCallingCode, setCountryCallingCode] = useState(null);
    useEffect(() => {
      let shouldSetCountryCallingCode = true;
      getCountryCallingCodeById(countryCallingCodeValue.id).then(
        (callingCode) => {
          if (shouldSetCountryCallingCode) {
            setCountryCallingCode(callingCode);
          }
        }
      );
      return () => {
        shouldSetCountryCallingCode = false;
      };
    }, [countryCallingCodeValue.id]);
    const commonCountryCallingCodeOptions = useCommonCountryCallingCodeOptions(language);
    const allCountryCallingCodeOptions = useAllCountryCallingCodeOptions(language);
    const dropdownOptions = useMemo(
      () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          "optgroup",
          {
            "data-testid": "commonCountryCodes",
            label: t("Commonly selected"),
            children: commonCountryCallingCodeOptions.map(mapTaxonomyToDropdown).map(mapDropdownItemToOption)
          }
        ),
        /* @__PURE__ */ jsx("optgroup", { "data-testid": "allCountryCodes", label: t("All"), children: allCountryCallingCodeOptions.map(mapTaxonomyToDropdown).map(mapDropdownItemToOption) })
      ] }),
      [allCountryCallingCodeOptions, commonCountryCallingCodeOptions, t]
    );
    return /* @__PURE__ */ jsxs(
      Columns,
      {
        space: ["small", "medium"],
        collapseBelow: "tablet",
        alignY: "bottom",
        children: [
          /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(
            Dropdown,
            {
              id: "countryCallingCode",
              label: t("Phone number"),
              onChange: (event) => onCountryCallingCodeChange({
                id: event.currentTarget.value
              }),
              value: countryCallingCodeValue.id,
              secondaryLabel,
              placeholder: t("Select country code"),
              reserveMessageSpace: true,
              children: dropdownOptions
            }
          ) }),
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
            TextField,
            {
              id: "phoneNumber",
              type: "tel",
              ref,
              placeholder: t("Enter phone number"),
              onChange: (event) => onPhoneNumberChange(event.currentTarget.value),
              data: {
                "hj-masked": "true",
                automation: "phone-number"
              },
              prefix: countryCallingCode ? `+${countryCallingCode}` : void 0,
              value: phoneNumber || "",
              message,
              tone,
              "aria-label": t("Phone number"),
              reserveMessageSpace: true
            }
          ) })
        ]
      }
    );
  }
);
PhoneNumberCountryCallingCode.displayName = "PhoneNumberCountryCallingCode";
const privacyLevelDescriptionMap = {
  [PrivacyLevel.STANDARD]: "Employers can view and download my Profile and...",
  [PrivacyLevel.LIMITED]: "Employers can view my Profile, but...",
  [PrivacyLevel.HIDDEN]: "Employers cannot search for me..."
};
const jobsDBPrivacyLevelDescriptionMap = {
  [PrivacyLevel.STANDARD]: "Highest chance for employers to contact you",
  [PrivacyLevel.LIMITED]: "Employers can invite you to apply for jobs",
  [PrivacyLevel.HIDDEN]: "Relevant employers cannot contact you with exciting opportunities"
};
const useLevelDescription = (isUnifiedSite, brand) => {
  const { t } = useTranslations(translations$9);
  return useCallback(
    (country, level) => isUnifiedSite || !["HK", "TH"].includes(country) ? t(privacyLevelDescriptionMap[level], { brand }) : t(jobsDBPrivacyLevelDescriptionMap[level]),
    [t, isUnifiedSite, brand]
  );
};
const getPrivacyPolicyPath = (country, language, isUnifiedSite = false) => {
  if (isUnifiedSite) {
    const languagePrefix = language !== "en" ? `/${language}` : "";
    return `${languagePrefix}/profile-privacy`;
  }
  switch (country) {
    case "AU":
    case "NZ":
    case "MY":
    case "SG":
    case "PH":
    case "ID":
      return "/profile-privacy";
    case "HK":
      return "/en-hk/pages/terms/privacy-policy/";
    case "TH":
      if (language === "th") {
        return "/th-th/pages/terms/privacy-policy/";
      }
      return "/en-th/pages/terms/privacy-policy/";
    default:
      return "/profile-privacy";
  }
};
const mapZoneBrandToBrand$1 = {
  seek: "SEEK",
  jobsdb: "Jobsdb",
  jobstreet: "Jobstreet",
  jora: "SEEK"
};
const PrivacyDropdown = ({
  zone = "anz-1",
  isUnifiedSite = false,
  level,
  dropdownToggleComponent = "select",
  message = "",
  valid = true,
  id = "",
  label = "Profile visibility",
  ...restProps
}) => {
  const { t } = useTranslations(translations$9);
  const language = useCompatibleLanguage();
  const [brand, country] = brandAndCountryFromZone(zone) || ["seek", "AU"];
  const acceptedBrand = mapZoneBrandToBrand$1[brand];
  const getLevelDescription = useLevelDescription(isUnifiedSite, acceptedBrand);
  const dropdownLabel = isUnifiedSite ? t("Your profile visibility setting controls...") : t("Your profile visibility setting controls legacy jobsdb...", {
    LineBreak: () => /* @__PURE__ */ jsx("br", {})
  });
  const displayLevel = level ? /* @__PURE__ */ jsx(Text, { weight: "strong", children: /* @__PURE__ */ jsx("span", { "data-automation": "visibility-level", "data-testid": "visibility-level", children: t(level) }) }) : /* @__PURE__ */ jsxs(Text, { tone: "critical", children: [
    /* @__PURE__ */ jsx(IconCritical, {}),
    ` `,
    /* @__PURE__ */ jsx("span", { "data-automation": "visibility-not-set", children: t("Visibility not set") })
  ] });
  const options = [
    {
      label: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsxs(Inline, { alignY: "center", space: "xsmall", children: [
          /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(Strong, { children: t("Standard") }) }),
          /* @__PURE__ */ jsx(Badge, { tone: "promote", weight: "strong", children: t("Recommended") })
        ] }),
        /* @__PURE__ */ jsx(Text, { tone: "secondary", children: /* @__PURE__ */ jsx(Box, { component: "p", children: getLevelDescription(country, PrivacyLevel.STANDARD) }) })
      ] }),
      value: PrivacyLevel.STANDARD
    },
    {
      label: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(Strong, { children: t("Limited") }) }),
        /* @__PURE__ */ jsx(Text, { tone: "secondary", children: /* @__PURE__ */ jsx(Box, { component: "p", children: getLevelDescription(country, PrivacyLevel.LIMITED) }) })
      ] }),
      value: PrivacyLevel.LIMITED
    },
    {
      label: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(Strong, { children: t("Hidden") }) }),
        /* @__PURE__ */ jsx(Text, { tone: "secondary", children: /* @__PURE__ */ jsx(Box, { component: "p", children: getLevelDescription(country, PrivacyLevel.HIDDEN) }) })
      ] }),
      value: PrivacyLevel.HIDDEN
    }
  ];
  const dropdown = /* @__PURE__ */ jsx(
    FancyDropdown,
    {
      value: level,
      selectionTypeString: t("profile visibility level"),
      dropdownToggleComponent,
      options,
      description: dropdownToggleComponent === "select" ? dropdownLabel : "",
      message,
      valid,
      id,
      label,
      placeholder: t("Select a profile visibility level"),
      ...restProps
    }
  );
  const isButton = dropdownToggleComponent === "button";
  return /* @__PURE__ */ jsxs(Stack, { space: isButton ? "none" : "small", children: [
    isButton ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: dropdownLabel }),
      /* @__PURE__ */ jsxs(Box, { display: "flex", justifyContent: "spaceBetween", alignItems: "center", children: [
        /* @__PURE__ */ jsx(Box, { paddingRight: "medium", children: displayLevel }),
        dropdown
      ] })
    ] }) : dropdown,
    /* @__PURE__ */ jsx(Text, { size: "small", children: t("For all settings, your Profile including...", {
      TextLink: (v) => /* @__PURE__ */ jsx(
        TextLink,
        {
          target: "_blank",
          href: getPrivacyPolicyPath(country, language, isUnifiedSite),
          hitArea: "large",
          children: v
        },
        "privacyPolicyPath"
      )
    }) })
  ] });
};
const CancelButton = ({
  handleCancel,
  dataAutomationCardName,
  cardName,
  cancelButtonText
}) => {
  const { t } = useTranslations(translations$a);
  return /* @__PURE__ */ jsx(
    Button,
    {
      tone: "formAccent",
      variant: "ghost",
      onClick: handleCancel,
      data: { automation: `cancel-delete-${dataAutomationCardName}` },
      children: cancelButtonText ? cancelButtonText : /* @__PURE__ */ jsx(Fragment, { children: t("Cancel delete card", {
        cardName,
        HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v }, "cancelDeleteCard")
      }) })
    }
  );
};
const toLowerHyphenate = (text) => text.replace(/\s+/g, "-").toLowerCase();
const ProfileFormCard = ({
  children,
  formWrapper: FormWrapper = Card,
  cardName,
  dismissOnly = false,
  mobileFullscreen = false,
  dataAutomationCardName = toLowerHyphenate(cardName),
  overlayTitle,
  overlayMessage,
  onPrimaryClick,
  onSecondaryClick,
  primaryButtonText,
  onCancelFocusElement,
  cancelButtonText
}) => {
  const { t } = useTranslations(translations$a);
  const [show, setShow] = useState(false);
  const showOverlay = () => setShow(true);
  const hideOverlay = () => setShow(false);
  const handlePrimaryClick = useCallback(() => {
    if (onPrimaryClick) {
      onPrimaryClick();
    }
    hideOverlay();
  }, [onPrimaryClick]);
  const handleSecondaryClick = useCallback(() => {
    if (onSecondaryClick) {
      onSecondaryClick();
    }
    hideOverlay();
    if (onCancelFocusElement) {
      focusElement(onCancelFocusElement);
    }
  }, [onSecondaryClick, onCancelFocusElement]);
  return /* @__PURE__ */ jsx(
    ProfileFormCardContent,
    {
      showOverlay: show,
      mobileFullscreen,
      overlayContent: /* @__PURE__ */ jsx(
        ProfileFormCardOverlayContent,
        {
          title: overlayTitle ? overlayTitle : t("Delete card", { cardName }),
          message: overlayMessage,
          focusMessage: show,
          actions: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                type: "submit",
                onClick: handlePrimaryClick,
                data: {
                  automation: `${dismissOnly ? "dismiss" : "confirm-delete"}-${dataAutomationCardName}`
                },
                tone: dismissOnly ? "formAccent" : "critical",
                children: primaryButtonText ? primaryButtonText : t("Delete")
              }
            ),
            dismissOnly ? null : /* @__PURE__ */ jsx(
              CancelButton,
              {
                handleCancel: handleSecondaryClick,
                dataAutomationCardName,
                cardName,
                cancelButtonText
              }
            )
          ] })
        }
      ),
      card: FormWrapper,
      showDeleteOverlay: showOverlay,
      children
    }
  );
};
const mapZoneBrandToBrand = {
  seek: "SEEK",
  jobsdb: "Jobsdb",
  jobstreet: "Jobstreet",
  jora: "SEEK"
};
const ProfileVisibilitySection = ({
  zone,
  value,
  onChange,
  id = "",
  message
}) => {
  const { t } = useTranslations(translations$b);
  const language = useCompatibleLanguage();
  const [brand] = brandAndCountryFromZone(zone) || ["seek", "AU"];
  const acceptedBrand = mapZoneBrandToBrand[brand];
  const getUrl = useGetUrl(zone, language, true);
  return /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(Heading, { level: "3", component: "h2", children: t("Be found by employers") }),
    /* @__PURE__ */ jsx(Text, { tone: "neutral", children: t("Your profile visibility setting controls...") }),
    /* @__PURE__ */ jsxs(
      RadioGroup,
      {
        id,
        "aria-label": t("Search settings"),
        onChange,
        value,
        data: { automation: "profile-visibility-options" },
        children: [
          /* @__PURE__ */ jsx(
            RadioItem,
            {
              data: {
                automation: "standard-radio-option",
                testid: "standard-radio-option"
              },
              badge: /* @__PURE__ */ jsx(Badge, { tone: "promote", children: t("Recommended") }),
              label: /* @__PURE__ */ jsx(Strong, { children: t("Standard") }),
              description: t("Employers can view my Profile and...", {
                brand: acceptedBrand
              }),
              value: "Standard"
            }
          ),
          /* @__PURE__ */ jsx(
            RadioItem,
            {
              data: {
                automation: "limited-radio-option",
                testid: "limited-radio-option"
              },
              label: /* @__PURE__ */ jsx(Strong, { children: t("Limited") }),
              description: t("Employers can view my Profile, but...", {
                brand: acceptedBrand
              }),
              value: "Limited"
            }
          ),
          /* @__PURE__ */ jsx(
            RadioItem,
            {
              data: {
                automation: "hidden-radio-option",
                testid: "hidden-radio-option"
              },
              label: /* @__PURE__ */ jsx(Strong, { children: t("Hidden") }),
              description: t("Employers cannot search for me..."),
              value: "Hidden"
            }
          )
        ]
      }
    ),
    message ? /* @__PURE__ */ jsx(
      FieldMessage,
      {
        ...{
          id: `${id}-message`,
          tone: "critical",
          message,
          data: { automation: "profile-visibility-message" }
        }
      }
    ) : null,
    /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: t("For all settings, your Profile including...", {
      TextLink: (v) => /* @__PURE__ */ jsx(
        TextLink,
        {
          target: "_blank",
          href: getUrl("profile-privacy"),
          hitArea: "large",
          children: v
        },
        "privacyPolicyPath"
      )
    }) })
  ] });
};
const emptySkill = { keyword: { text: "", ontologyId: null } };
const initialValues = {
  skill: emptySkill
};
const INPUT_FIELD_NAME = "skill";
const FORM_NAME = "skill-input";
const { FormProvider: FormProvider$2, useField: useField$2 } = createForm(FORM_NAME, {
  formType: "braid"
});
const onSubmitHandler = ({
  skillInputValue: { skill, isValid },
  skills,
  suggestedSkills,
  onSubmit,
  hasTooManySkills
}) => (event) => {
  event.persist();
  if (!hasTooManySkills && isValid) {
    const reverseArray = [...skills];
    if (skill.keyword.text !== "") {
      reverseArray.unshift(skill);
    }
    reverseArray.reverse();
    onSubmit({
      values: reverseArray,
      event,
      suggestedSkillsAdded: suggestedSkills.length ? suggestedSkills.length : null
    });
  }
};
const Form$2 = ({
  locale = "AU",
  initialSkills = [],
  initialSuggestedSkills = [],
  onSubmit = () => {
  },
  onCancel = () => {
  },
  inputRef,
  resetForm,
  setFieldValue,
  handleSubmit,
  scrollToTopOnMobile = true,
  apolloClient,
  onFormDirtyChange
}) => {
  const { t } = useTranslations(translations$c);
  const initialRender = useRef(true);
  const [skills, setSkills] = useState([...initialSkills].reverse());
  const [skillsDirty, setSkillsDirty] = useState(false);
  const [suggestedSkills, setSuggestedSkills] = useState(
    getSuggestedSkills({ initialSuggestedSkills, initialSkills })
  );
  const [selectedSuggestedSkills, setSelectedSuggestedSkills] = useState([]);
  const { validators, validatorProps } = rules.skills();
  const hasMaxSkills = skills.length >= MAX_SKILLS;
  const hasTooManySkills = skills.length > MAX_SKILLS;
  const excessSkills = skills.length - MAX_SKILLS;
  const maximumSkills = MAX_SKILLS.toString();
  useEffect(() => {
    if (onFormDirtyChange && !initialRender.current) {
      onFormDirtyChange(skillsDirty);
    }
    return () => {
      if (onFormDirtyChange && skillsDirty) onFormDirtyChange(false);
    };
  }, [skillsDirty, onFormDirtyChange]);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const {
    onChange: skillFieldOnChange,
    value,
    ...skillField
  } = useField$2({
    id: INPUT_FIELD_NAME,
    validators: [...validators, maxSkills],
    validatorProps: { ...validatorProps, hasMaxSkills },
    getValidationValue: getValidationValue$2
  });
  const updateSkills = useCallback(
    (newSkill) => {
      const skillIsNotEmpty = newSkill.keyword.text.trim() !== "";
      if (skillIsNotEmpty) {
        const newSkills = [newSkill, ...skills];
        setSkills(newSkills);
        setSkillsDirty(true);
      }
    },
    [skills]
  );
  const clearSkill = (skillIndex) => {
    const newSkills = skills.filter((_, index) => index !== skillIndex);
    const clearedSkill = skills[skillIndex];
    setSkills(newSkills);
    setSkillsDirty(true);
    if (selectedSuggestedSkills.includes(clearedSkill)) {
      const newSelectedSuggestedSkills = selectedSuggestedSkills.filter(
        (skill) => skill.keyword.text !== clearedSkill.keyword.text
      );
      setSelectedSuggestedSkills(newSelectedSuggestedSkills);
      setSuggestedSkills([clearedSkill, ...suggestedSkills]);
    }
    if (skills.length >= MAX_SKILLS) {
      setTimeout(() => {
        skillFieldOnChange(value);
        skillField.onBlur();
      });
    }
  };
  const addSuggestedSkill = useCallback(
    (skill) => {
      setSelectedSuggestedSkills([...selectedSuggestedSkills, skill]);
      setSuggestedSkills(
        suggestedSkills.filter(
          (suggestedSkill) => skill.keyword.text !== suggestedSkill.keyword.text
        )
      );
      updateSkills(skill);
    },
    [selectedSuggestedSkills, suggestedSkills, updateSkills]
  );
  const handleOnChange = useCallback(
    ({ isSuggestionSelect }) => ({ text, ontologyId }) => {
      const skill = {
        keyword: {
          text,
          ontologyId: ontologyId ? ontologyId : null
        }
      };
      if (isSuggestionSelect && !hasMaxSkills) {
        resetForm();
        updateSkills(skill);
      } else {
        skillFieldOnChange(skill);
      }
    },
    [hasMaxSkills, resetForm, skillFieldOnChange, updateSkills]
  );
  const showSuggestedSkills = initialSuggestedSkills.length > 0;
  const outerStackSpacing = showSuggestedSkills ? "xlarge" : "large";
  return /* @__PURE__ */ jsx(
    "form",
    {
      onSubmit: handleSubmit((formValues) => {
        updateSkills(formValues.skill);
        resetForm();
      }),
      children: /* @__PURE__ */ jsxs(Stack, { space: outerStackSpacing, children: [
        /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
          /* @__PURE__ */ jsx(Columns, { space: "none", collapseBelow: "tablet", children: /* @__PURE__ */ jsx(Column, { width: "2/3", children: /* @__PURE__ */ jsxs(Columns, { space: "xxsmall", alignY: "center", children: [
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
              ProfileAutosuggest,
              {
                id: INPUT_FIELD_NAME,
                label: t("Add new skill"),
                onClear: () => setFieldValue("skill", initialValues.skill),
                placeholder: t("e.g. Team building"),
                value: { text: value.keyword.text },
                data: {
                  automation: "skills-tags-input",
                  "hj-masked": "true"
                },
                asset: "SpecReq",
                queryVariables: {
                  profileField: "Skills"
                },
                scrollToTopOnMobile,
                onChange: handleOnChange({ isSuggestionSelect: false }),
                onSuggestionSelect: handleOnChange({
                  isSuggestionSelect: true
                }),
                getStructuredData: getSkillsStructuredData,
                inputRef,
                locale,
                apolloClient,
                ...skillField,
                message: !skillField.valid ? skillField.errorMessage : t("Click add or press enter")
              }
            ) }),
            /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
              Button,
              {
                tone: "formAccent",
                type: "submit",
                data: { automation: "add-skill-button" },
                children: t("Add")
              }
            ) })
          ] }) }) }),
          hasTooManySkills ? /* @__PURE__ */ jsx(Alert, { tone: "critical", children: /* @__PURE__ */ jsx(Text, { children: t("No more than maximumSkills allowed...", {
            maximumSkills,
            excessSkills
          }) }) }) : null
        ] }),
        showSuggestedSkills ? /* @__PURE__ */ jsx(
          SuggestedSkills,
          {
            suggestedSkills,
            onClick: addSuggestedSkill,
            hasMaxSkills
          }
        ) : null,
        /* @__PURE__ */ jsx(Skills, { skills, clearSkill }),
        /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t(
          "Stay safe. Don’t include sensitive personal information such as..."
        ) }),
        /* @__PURE__ */ jsxs(Actions, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              tone: "formAccent",
              onClick: onSubmitHandler({
                skillInputValue: { skill: value, isValid: skillField.valid },
                skills,
                suggestedSkills: selectedSuggestedSkills,
                onSubmit,
                hasTooManySkills
              }),
              data: { automation: "skills-save-button" },
              children: t("Save skills", {
                HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v }, "saveSkills")
              })
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              tone: "formAccent",
              variant: "transparent",
              onClick: onCancel,
              data: { automation: "skills-cancel-button" },
              children: t("Cancel skills", {
                HiddenVisually: (v) => /* @__PURE__ */ jsx(HiddenVisually, { children: v }, "cancelSkills")
              })
            }
          )
        ] })
      ] })
    }
  );
};
const SkillsForm = (props) => {
  const language = useCompatibleLanguage();
  return /* @__PURE__ */ jsx(FormProvider$2, { initialValues, language, children: ({ handleSubmit, resetForm, setFieldValue }) => /* @__PURE__ */ jsx(
    Form$2,
    {
      handleSubmit,
      resetForm,
      setFieldValue,
      ...props
    }
  ) });
};
const VerifiedForm = ({
  credentialInfo,
  name: verificationName,
  issuingOrganisation,
  formattedDate,
  onManageClick,
  onCertsyClick,
  manageVerificationUrl,
  title,
  credentialIsRenewable = false,
  seekPassLogo = /* @__PURE__ */ jsx(VerificationLogo, {})
}) => {
  const { t } = useTranslations(translations$d);
  const [displayDialog, setDisplayDialog] = useState(false);
  const { openDialog, closeDialog } = useDialogHistoryManager(setDisplayDialog);
  const translatedTitle = title || t("Manage your verificationName", { verificationName });
  const onClose = useCallback(() => {
    closeDialog();
  }, [closeDialog]);
  const onManageClickFn = useCallback(
    (event) => {
      onManageClick == null ? void 0 : onManageClick(event);
      openDialog();
    },
    [onManageClick, openDialog]
  );
  const manageVerificationDialogBodyText = credentialIsRenewable ? t("Renew, remove or edit your verification on SEEK Pass.") : t("Remove or edit your verification on SEEK Pass.");
  return /* @__PURE__ */ jsxs(Box, { boxShadow: "borderNeutralLight", borderRadius: "large", padding: "gutter", children: [
    /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsxs(Inline, { space: "xsmall", children: [
          /* @__PURE__ */ jsx(Text, { weight: "strong", children: verificationName }),
          /* @__PURE__ */ jsx(CertsyVerifiedIndicator, { idSuffix: verificationName })
        ] }),
        issuingOrganisation ? /* @__PURE__ */ jsx(Text, { children: issuingOrganisation }) : null
      ] }),
      formattedDate ? /* @__PURE__ */ jsx(Text, { tone: "secondary", children: formattedDate }) : null,
      /* @__PURE__ */ jsx(DefinitionList, { data: credentialInfo, space: "small" }),
      manageVerificationUrl ? /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
        TextLinkButton,
        {
          hitArea: "large",
          onClick: onManageClickFn,
          icon: /* @__PURE__ */ jsx(IconNewWindow, { alignY: "lowercase" }),
          iconPosition: "trailing",
          children: t("Manage on SEEK Pass")
        }
      ) }) : null
    ] }),
    manageVerificationUrl && /* @__PURE__ */ jsx(
      CertsyDialog,
      {
        bodyText: manageVerificationDialogBodyText,
        certsyButtonText: t("Manage on SEEK Pass"),
        certsyUrl: manageVerificationUrl,
        onCertsyClick,
        title: translatedTitle,
        open: displayDialog,
        onClose,
        width: "small",
        seekPassLogo
      }
    )
  ] });
};
const mapFormStateToProps$1 = ({
  form: { dirty }
}) => ({
  dirty
});
const LICENCE_FORM = "LICENCE_FORM";
const { FormProvider: FormProvider$1, useField: useField$1 } = createForm(LICENCE_FORM, { formType: "braid" });
const descriptionRules = rules.licenceDescription();
const Form$1 = ({
  onSubmit,
  onCancel,
  dirty,
  onFormDirtyChange,
  handleSubmit,
  name,
  issuingOrganisation,
  formattedDate,
  credentialInfo,
  status,
  manageVerificationUrl,
  verificationMessage,
  onManageClick,
  onCertsyClick,
  onDelete,
  readonly
}) => {
  const { t } = useTranslations(translations$e);
  const initialRender = useRef(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };
  useEffect(() => {
    if (onFormDirtyChange && !initialRender.current) {
      onFormDirtyChange(dirty);
    }
    return () => {
      if (onFormDirtyChange && dirty) onFormDirtyChange(false);
    };
  }, [dirty, onFormDirtyChange]);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const onSubmitFn = useCallback(
    () => handleSubmit((values) => {
      if (onSubmit) {
        onSubmit({
          dirty,
          values: { description: values == null ? void 0 : values.description }
        });
      }
    }),
    [handleSubmit, onSubmit, dirty]
  );
  const descriptionField = useField$1({
    id: "description",
    ...descriptionRules
  });
  const isExpired2 = status === CredentialStatus.Expired;
  return /* @__PURE__ */ jsxs("form", { children: [
    /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        isExpired2 && /* @__PURE__ */ jsx(Alert, { tone: "info", children: /* @__PURE__ */ jsx(Text, { children: t("Expired SEEK Pass verifications are only visible to you") }) }),
        /* @__PURE__ */ jsx(
          VerifiedForm,
          {
            title: verificationMessage,
            name,
            issuingOrganisation,
            formattedDate,
            credentialInfo,
            status,
            onManageClick,
            onCertsyClick,
            manageVerificationUrl,
            credentialIsRenewable: true,
            seekPassLogo: /* @__PURE__ */ jsx(VerificationLogo, {})
          }
        ),
        !readonly && /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(
            VerifiedDescription,
            {
              id: "description",
              type: "licence",
              label: t("Description"),
              secondaryLabel: t("optional"),
              description: t(
                "Briefly describe this credential – you can also add a type or URL if applicable."
              ),
              descriptionField
            }
          ),
          /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t(
            "Stay safe. Don’t include sensitive personal information such as..."
          ) })
        ] })
      ] }),
      /* @__PURE__ */ jsx(
        FormActionButtons,
        {
          cardName: t("credential"),
          dataAutomationSuffix: "credential",
          onCancel,
          onSubmit: onSubmitFn(),
          readonly,
          ...onDelete && { onDeleteClick }
        }
      )
    ] }),
    onDelete && /* @__PURE__ */ jsx(
      DeleteConfirmationDialog,
      {
        displayDialog: showDeleteDialog,
        setDisplayDialog: setShowDeleteDialog,
        onDelete,
        cardName: t("credential"),
        dataAutomationSuffix: "credential",
        verifiedType: "credential"
      }
    )
  ] });
};
const VerifiedLicenceForm = (props) => /* @__PURE__ */ jsx(
  FormProvider$1,
  {
    mapFormStateToProps: mapFormStateToProps$1,
    initialValues: getVerifiedInitialState(props.initialValues),
    onSubmitFail: props.onSubmitFail ? props.onSubmitFail : (errors) => scrollToError(errors),
    validateOnMount: props.validateOnMount,
    children: (formProps) => /* @__PURE__ */ jsx(Form$1, { ...formProps, ...props })
  }
);
const mapFormStateToProps = ({
  form: { dirty }
}) => ({
  dirty
});
const EDUCATION_FORM = "EDUCATION_FORM";
const { FormProvider, useField } = createForm(EDUCATION_FORM, { formType: "braid" });
const highlightsRules = rules.highlights();
const Form = ({
  onSubmit,
  onCancel,
  dirty,
  onFormDirtyChange,
  handleSubmit,
  name,
  credentialInfo,
  status,
  manageVerificationUrl,
  onManageClick,
  onCertsyClick,
  onDelete,
  readonly,
  issuingOrganisation
}) => {
  const { t } = useTranslations(translations$f);
  const initialRender = useRef(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  useEffect(() => {
    if (onFormDirtyChange && !initialRender.current) {
      onFormDirtyChange(dirty);
    }
  }, [dirty, onFormDirtyChange]);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const onSubmitFn = useCallback(
    () => handleSubmit((values) => {
      if (onSubmit) {
        onSubmit({ dirty, values: { highlights: values == null ? void 0 : values.highlights } });
      }
    }),
    [handleSubmit, onSubmit, dirty]
  );
  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };
  const highlightsField = useField({
    id: "highlights",
    ...highlightsRules
  });
  return /* @__PURE__ */ jsxs("form", { children: [
    /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsx(
          VerifiedForm,
          {
            title: t("Manage your education"),
            name,
            credentialInfo,
            status,
            onManageClick,
            onCertsyClick,
            manageVerificationUrl,
            credentialIsRenewable: false,
            issuingOrganisation,
            seekPassLogo: /* @__PURE__ */ jsx(EducationLogo, {})
          }
        ),
        !readonly && /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(
            VerifiedDescription,
            {
              id: "highlights",
              type: "qualification",
              label: t("Course highlights"),
              secondaryLabel: t("optional"),
              description: t(
                "Add activities, projects, awards or achievements during your study."
              ),
              descriptionField: highlightsField
            }
          ),
          /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t(
            "Stay safe. Don’t include sensitive personal information such as..."
          ) })
        ] })
      ] }),
      /* @__PURE__ */ jsx(
        FormActionButtons,
        {
          cardName: t("verified education"),
          dataAutomationSuffix: "verified-education",
          onCancel,
          onSubmit: onSubmitFn(),
          readonly,
          ...onDelete && { onDeleteClick }
        }
      )
    ] }),
    onDelete && /* @__PURE__ */ jsx(
      DeleteConfirmationDialog,
      {
        displayDialog: showDeleteDialog,
        setDisplayDialog: setShowDeleteDialog,
        onDelete,
        cardName: t("verified education"),
        dataAutomationSuffix: "verified-education",
        verifiedType: "education"
      }
    )
  ] });
};
const VerifiedEducationForm = (props) => /* @__PURE__ */ jsx(
  FormProvider,
  {
    mapFormStateToProps,
    initialValues: getVerifiedInitialState$1(props.initialValues),
    onSubmitFail: props.onSubmitFail ? props.onSubmitFail : (errors) => scrollToError(errors),
    validateOnMount: props.validateOnMount,
    children: (formProps) => /* @__PURE__ */ jsx(Form, { ...formProps, ...props })
  }
);
const YearsOfExperience = ({
  id,
  value: { option, newToWorkforce },
  onChange,
  tone,
  message,
  hideNewToWorkforce = false
}) => {
  const { t } = useTranslations(translations$g);
  const [yearOptions, setYearOptions] = useState([]);
  const language = useCompatibleLanguage();
  useEffect(() => {
    yearsOfExperienceOptions(language).then((options) => {
      setYearOptions(options);
    });
  }, [language]);
  return /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    !hideNewToWorkforce && /* @__PURE__ */ jsx(
      Toggle,
      {
        id: `${id}-toggle`,
        label: t("I am new to the workforce"),
        on: newToWorkforce,
        onChange: (newValue) => onChange({ option, newToWorkforce: newValue }),
        bleedY: true
      }
    ),
    !newToWorkforce ? /* @__PURE__ */ jsx(
      Dropdown,
      {
        id: `${id}-dropdown`,
        label: t("Years of experience"),
        description: t("Specify the total number of years of your career"),
        placeholder: t("Please select"),
        data: { automation: "years-of-experience" },
        value: (option == null ? void 0 : option.id) || "",
        onChange: (event) => onChange({
          option: { id: event.currentTarget.value },
          newToWorkforce
        }),
        tone,
        message,
        reserveMessageSpace: true,
        children: yearOptions.map(mapTaxonomyToDropdown).map(mapDropdownItemToOption)
      }
    ) : null
  ] });
};
export {
  CareerHistoryForm,
  ClassificationDropdown,
  DeleteConfirmationDialog,
  EducationForm,
  FormActionButtons,
  LicenceForm,
  NationalityDropdown,
  PhoneNumberCountryCallingCode,
  PrivacyDropdown,
  ProfileAutosuggest,
  ProfileFormCard,
  ProfileFormCardContent,
  ProfileFormCardOverlayContent,
  ProfileMonthPickerRenderer,
  ProfileMonthPickerToggleRenderer,
  ProfileVisibilitySection,
  SkillsForm,
  VerifiedEducationForm,
  VerifiedForm,
  VerifiedLicenceForm,
  YearsOfExperience,
  mapDropdownItemToOption,
  mapDropdownItemsToOptgroup
};
